'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import type { PricingTier, BillingType } from '@/types';
import { captureAttribution, clearAttribution, hasFullConsent } from '@/lib/attribution';

const ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
const PURCHASE_LABEL = process.env.NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_LABEL;
const LEAD_LABEL = process.env.NEXT_PUBLIC_GOOGLE_ADS_LEAD_LABEL;

// The Google Ads account is EUR-denominated, but Stripe genuinely charges
// customers in USD, so USD is the correct currency for every event below --
// it must match what was actually charged, not the account's default currency.
// Verified against the two conversion actions' config: SendVC Purchase has
// always_use_default_value=false, so it uses whatever currency the tag sends
// (must stay USD); SendVC Lead Capture has always_use_default_value=true at
// EUR 5, so the currency this file sends for lead events is ignored entirely.
const GA4_CURRENCY = 'USD';

type GtagFn = (...args: unknown[]) => void;

function gtag(): GtagFn | null {
  if (typeof window === 'undefined') return null;
  const fn = (window as unknown as { gtag?: GtagFn }).gtag;
  return typeof fn === 'function' ? fn : null;
}

export function trackEvent(
  action: string,
  category: string,
  label?: string,
  value?: number
) {
  const g = gtag();
  if (!g) return;
  g('event', action, {
    event_category: category,
    event_label: label,
    value,
  });
}

export function trackButtonClick(buttonName: string) {
  trackEvent('button_click', 'engagement', buttonName);
}

type AuthMethod = 'password' | 'google' | 'magic_link';

// GA4's two recommended auth events. Neither existed before: all four handlers
// in app/(routes)/login/page.tsx completed silently, so the GA4 funnel began at
// begin_checkout with nothing above it and no way to see signup drop-off.
export function trackSignUp(method: AuthMethod) {
  const g = gtag();
  if (!g) return;
  g('event', 'sign_up', { method });
}

export function trackLogin(method: AuthMethod) {
  const g = gtag();
  if (!g) return;
  g('event', 'login', { method });
}

// Deliberately NOT `login`. Requesting a magic link only sends the mail — the
// session is created later, when the recipient clicks the link. Firing `login`
// here would count people who never open the mail, so this is a separate
// engagement event and the real `login` fires on the callback page.
export function trackMagicLinkRequested() {
  trackEvent('magic_link_requested', 'engagement', 'login_page');
}

export function trackPricingSelection(tier: string, billing: string, abVariant?: string) {
  const g = gtag();
  if (!g) return;
  g('event', 'pricing_selection', {
    event_category: 'conversion',
    event_label: `${tier}_${billing}`,
    ...(abVariant ? { ab_test: 'pricing_anchor', ab_variant: abVariant } : {}),
  });
}

// --- Lightweight A/B testing (localStorage-persisted 50/50 split) ---
//
// Variant assignment is sticky per browser; results are read in GA4 by
// segmenting events on the ab_test / ab_variant event parameters
// (ab_exposure = who saw which copy, pricing_selection / begin_checkout /
// purchase = how each group converted).

export type AbVariant = 'control' | 'variant';

export function getAbVariant(testName: string): AbVariant {
  if (typeof window === 'undefined') return 'control';
  // The assignment is stored on the device, so it needs marketing consent;
  // without it every visitor sees the control and nothing is read or written.
  if (!hasFullConsent()) return 'control';
  const key = `ab_${testName}`;
  try {
    const existing = window.localStorage.getItem(key);
    if (existing === 'control' || existing === 'variant') return existing;
    const assigned: AbVariant = Math.random() < 0.5 ? 'control' : 'variant';
    window.localStorage.setItem(key, assigned);
    return assigned;
  } catch {
    // Storage unavailable (private mode etc.) -> stable default, no test noise
    return 'control';
  }
}

export function trackAbExposure(testName: string, variant: AbVariant) {
  const g = gtag();
  if (!g) return;
  g('event', 'ab_exposure', { ab_test: testName, ab_variant: variant });
}

export function trackBeginCheckout(tier: string, value: number, billing: string) {
  const g = gtag();
  if (!g) return;
  g('event', 'begin_checkout', {
    currency: GA4_CURRENCY,
    value,
    items: [
      {
        item_id: `${tier}_${billing}`,
        item_name: `SendVC ${tier} ${billing}`,
        price: value,
        quantity: 1,
      },
    ],
  });
}

export function trackPurchase(tier: string, value: number, transactionId: string, billing = 'monthly') {
  const g = gtag();
  if (!g) return;

  g('event', 'purchase', {
    transaction_id: transactionId,
    value,
    currency: GA4_CURRENCY,
    items: [
      {
        item_id: `${tier}_${billing}`,
        item_name: `SendVC ${tier} ${billing}`,
        price: value,
        quantity: 1,
      },
    ],
  });

  if (ADS_ID && PURCHASE_LABEL) {
    g('event', 'conversion', {
      send_to: `${ADS_ID}/${PURCHASE_LABEL}`,
      value,
      currency: GA4_CURRENCY,
      transaction_id: transactionId,
    });
  }
}

export function trackLeadCapture(value = 5, transactionId?: string) {
  const g = gtag();
  if (!g) return;

  g('event', 'generate_lead', {
    currency: GA4_CURRENCY,
    value,
    transaction_id: transactionId,
  });

  if (ADS_ID && LEAD_LABEL) {
    g('event', 'conversion', {
      send_to: `${ADS_ID}/${LEAD_LABEL}`,
      value,
      currency: GA4_CURRENCY,
      transaction_id: transactionId,
    });
  }
}

// Fired when a visitor downloads a lead-magnet asset. `assetId` and `source`
// are non-PII identifiers only — never pass an email address here.
//
// `extension` defaults to 'pptx' because the deck templates were the first and
// only caller, but it must be passed for anything else — the free VC list is a
// PDF, and a hardcoded extension silently mislabels it in GA4.
export function trackAssetDownload(
  assetId: string,
  source: string,
  gated: boolean,
  extension = 'pptx'
) {
  const g = gtag();
  if (!g) return;
  g('event', 'file_download', {
    file_name: assetId,
    file_extension: extension,
    link_id: source,
    gated,
  });
}

// TrackingScripts' gtag('config', ..., { send_page_view: true }) only fires
// once, on the initial full document load. This is an App Router SPA, so
// every route change after that is a soft navigation the GA4 tag never sees
// on its own -- this component is what's missing to cover those.
//
// Must be rendered inside a <Suspense> boundary by the consumer: useSearchParams()
// opts the tree into client-side rendering and throws outside Suspense.
// Mount once, near the root, e.g. in app/layout.tsx:
//
//   import { Suspense } from 'react';
//   import { PageViewTracker } from '@/components/Analytics';
//   ...
//   <Suspense fallback={null}>
//     <PageViewTracker />
//   </Suspense>
export function PageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFirstRender = useRef(true);

  // Capture first-touch attribution here rather than in a single page, because
  // this component is mounted in the root layout and therefore runs on EVERY
  // landing page. It previously lived only in HomeContent, which meant an ad
  // click landing on /free-vc-list -- where roughly three quarters of paid
  // traffic goes -- recorded no utm/gclid/referrer at all.
  // Without marketing consent nothing may sit on the device: capture only with
  // consent, and drop a record left from before consent was withdrawn/declined.
  useEffect(() => {
    if (hasFullConsent()) captureAttribution();
    else clearAttribution();
  }, []);

  useEffect(() => {
    // Skip the mount-time run -- the initial page_view was already sent by
    // TrackingScripts' send_page_view: true config call. Firing here too
    // would double-count the very first pageview of every session.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const g = gtag();
    if (!g) return;
    const search = searchParams.toString();
    g('event', 'page_view', {
      page_path: search ? `${pathname}?${search}` : pathname,
      page_location: window.location.href,
    });
  }, [pathname, searchParams]);

  return null;
}

// --- GA4 ecommerce funnel (pricing page) ---
//
// Ids and cent prices below come from types/index.ts PRICING_TIERS -- these
// functions don't hardcode tier data, they just shape whatever tiers/tier the
// caller passes into GA4's items[] schema.
//
// item_id matches trackBeginCheckout/trackPurchase's `${tier}_${billing}`
// convention (once a billing cycle is known) so the four events reconcile
// as one item-scoped funnel in GA4 reporting instead of looking like
// different products.
const PRICING_ITEM_LIST_ID = 'pricing_tiers';
const PRICING_ITEM_LIST_NAME = 'Pricing Tiers';

function tierToItem(tier: PricingTier, billing?: BillingType) {
  const priceCents = billing === 'yearly' ? tier.priceYearly : tier.priceMonthly;
  return {
    item_id: billing ? `${tier.id}_${billing}` : tier.id,
    item_name: `SendVC ${tier.name}`,
    price: priceCents / 100,
    item_list_id: PRICING_ITEM_LIST_ID,
    item_list_name: PRICING_ITEM_LIST_NAME,
    quantity: 1,
  };
}

// Fire once when the pricing tiers become visible. No billing param: the
// list is shown before a billing cycle is chosen, so item_id is the bare
// tier id and price falls back to the monthly rate (the page's default
// selection) -- see PricingContent.tsx's `useState<BillingType>('monthly')`.
export function trackViewItemList(tiers: PricingTier[]) {
  const g = gtag();
  if (!g) return;
  g('event', 'view_item_list', {
    item_list_id: PRICING_ITEM_LIST_ID,
    item_list_name: PRICING_ITEM_LIST_NAME,
    items: tiers.map((tier, index) => ({ ...tierToItem(tier), index })),
  });
}

// Fire when a visitor picks a tier (the pricing-page CTA click). Billing is
// known here, so item_id carries the `_monthly` / `_yearly` suffix that
// trackBeginCheckout/trackPurchase also use.
export function trackSelectItem(tier: PricingTier, billing: BillingType) {
  const g = gtag();
  if (!g) return;
  g('event', 'select_item', {
    item_list_id: PRICING_ITEM_LIST_ID,
    item_list_name: PRICING_ITEM_LIST_NAME,
    items: [tierToItem(tier, billing)],
  });
}
